import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL, JOB_APPLICATIONS_SHEET_URL } from "./constants/adminConstants";
import "./css/adminStyles.css";

// ── Scrollable table wrapper with left/right scroll buttons ────────────

function TableWithScrollButtons({ children }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap">
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}

const JOBS_API = API_BASE_URL.replace("/tvc", "");
const SERVER_BASE = JOBS_API.replace("/api/v1", "");

const fixResumeUrl = (url) => {
  if (!url) return "";
  return url;
};

const getResumeUrl = (app) => {
  if (!app || !app.resume_path) return null;
  if (app.resume_path.startsWith("http")) {
    const token = sessionStorage.getItem("tvc_token");
    return `${JOBS_API}/jobs/applications/${app.id}/resume?token=${encodeURIComponent(token || "")}`;
  }
  return `${SERVER_BASE}${app.resume_path}`;
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];

// ── Dynamic column helpers ──

const FIELD_TO_COLUMN = {
  fullName: "full_name",
  email: "email",
  phone: "phone",
  linkedIn: "linkedin_url",
  experience: "years_of_experience",
  availability: "availability",
  compensation: "current_compensation",
  currentCompensation: "current_compensation",
  workPreference: "work_preference",
};

const trim = (s) => (s || "").trim();

const camelToSnake = (str) =>
  str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

const getColumnName = (key) => FIELD_TO_COLUMN[trim(key)] || camelToSnake(trim(key));

// Column names that map to fixed DB columns (not form_data)
const KNOWN_COL_NAMES = new Set([
  "full_name", "email", "phone", "linkedin_url",
  "years_of_experience", "availability", "current_compensation",
  "work_preference",
]);

const getFormValue = (app, field) => {
  const trimmedKey = trim(field.key);
  const colName = getColumnName(trimmedKey);
  // 1. Check dedicated DB column first (for known/core fields)
  if (KNOWN_COL_NAMES.has(colName) && app[colName] != null) {
    return app[colName];
  }
  // 2. Check form_data with trimmed key (new records)
  if (app.form_data) {
    if (app.form_data[trimmedKey] != null) return app.form_data[trimmedKey];
    if (field.key !== trimmedKey && app.form_data[field.key] != null) return app.form_data[field.key];
  }
  // 3. Try the computed column name anyway
  if (app[colName] != null) return app[colName];
  // 4. Fallback for renamed core field keys (e.g. fullName→applicantName):
  //    data is still in the original DB column, check all known columns
  if (!KNOWN_COL_NAMES.has(colName)) {
    for (const knownCol of KNOWN_COL_NAMES) {
      if (app[knownCol] != null) return app[knownCol];
    }
  }
  return null;
};

function AdminJobApplications({ user, onBack }) {
  const [formFields, setFormFields] = useState([]);

  // Fetch formFields from published visaCareer content
  useEffect(() => {
    const token = sessionStorage.getItem("tvc_token");
    if (!token) return;
    fetch(`${API_BASE_URL}/content/visaCareer`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const fields = (data?.section?.content?.formFields || []).map(f => ({
          ...f,
          key: (f.key || "").trim(),
          label: (f.label || "").trim(),
        }));
        setFormFields(fields);
      })
      .catch(() => {});
  }, []);

  // Column order follows formFields sequence from Visa Career Content editor
  const columnOrder = React.useMemo(() => {
    const skip = new Set(["resume_path", "resume_original_name", "id", "job_id", "updated_at"]);
    const order = [];
    for (const f of formFields) {
      const colName = getColumnName(f.key);
      if (skip.has(colName)) continue;
      order.push({ key: f.key, colName, label: f.label.replace(/\s*\*$/, ""), type: f.type });
    }
    return order;
  }, [formFields]);

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);

  const token = sessionStorage.getItem("tvc_token");

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const res = await fetch(`${JOBS_API}/jobs/applications${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setApplications(data.data || []);
      } else {
        setActionMsg(data.message || data.error || "Failed to load applications.");
      }
    } catch (err) {
      setActionMsg("Network error loading applications.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const handleStatusChange = async (id, newStatus) => {
    setActionMsg("");
    try {
      const res = await fetch(`${JOBS_API}/jobs/applications/${id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg(`Application marked as ${newStatus}.`);
        fetchApps();
      } else {
        setActionMsg(data.error || "Failed to update status.");
      }
    } catch (err) {
      setActionMsg("Network error updating status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    setActionMsg("");
    try {
      const res = await fetch(`${JOBS_API}/jobs/applications/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg("Application deleted.");
        fetchApps();
      } else {
        setActionMsg(data.error || "Failed to delete.");
      }
    } catch (err) {
      setActionMsg("Network error deleting application.");
    }
  };

  const handleOpenSheet = () => {
    window.open(JOB_APPLICATIONS_SHEET_URL, "_blank", "noopener,noreferrer");
  };

  const [pushingToSheet, setPushingToSheet] = useState(false);

  const handlePushToSheet = async () => {
    setPushingToSheet(true);
    setActionMsg("");
    try {
      const res = await fetch(`${JOBS_API}/jobs/applications/push-to-sheet`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setActionMsg("Job applications pushed to Google Sheet successfully.");
      } else {
        setActionMsg(data.error || "Failed to push to sheet.");
      }
    } catch (err) {
      setActionMsg("Network error pushing to sheet.");
    } finally {
      setPushingToSheet(false);
    }
  };
  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const filteredApps = applications.filter((a) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (a.full_name || "").toLowerCase().includes(q)
      || (a.email || "").toLowerCase().includes(q)
      || (a.job_title || "").toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="AdminCard">
        <div className="AdminCardHeader">
           <h3 className="AdminFooterH3" style={{ marginRight: "auto" }}>Application <span className="AdminCountBadge">{applications.length}</span></h3>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>
              Google Sheet
            </button>
            <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={pushingToSheet} title="Push to Sheet">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="AdminFilterBar" style={{ flexWrap: "nowrap", marginBottom: "24px", justifyContent: "flex-end", alignItems: "center" }}>
          <span style={{ marginRight: "auto", fontWeight: 600, fontSize: 15, color: "#1B2A4A" }}>Application</span>
          <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`} style={{ flexShrink: 0 }}>
            <span className="AdminPillSearchIcon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              ref={searchRef}
              type="text"
              className="AdminPillSearch"
              placeholder="Search name, email, job..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
               onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
            />
            {!searchOpen && (
              <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
            )}
          </div>
          <div className="AdminPillFilterItem">
            <select className="AdminPillSelect" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <select className="AdminPillSelect" style={{ width: 100 }} value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>

        {actionMsg && (
          <p className={`AdminMsgBox ${actionMsg.includes("error") || actionMsg.includes("Failed") ? "AdminError" : "AdminSuccess"}`}>
            {actionMsg}
          </p>
        )}

        {loading ? (
          <div className="AdminEmptyState">Loading applications...</div>
        ) : filteredApps.length === 0 ? (
          <div className="AdminEmptyState">No applications found.</div>
        ) : (
          <TableWithScrollButtons>
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th style={{ minWidth: 150 }}>Applied</th>
                  {columnOrder.map(col => {
                    const colMinW = col.colName === "full_name" ? 160
                      : col.colName === "email" ? 200
                      : col.colName === "phone" ? 140
                      : col.colName === "linkedin_url" ? 150
                      : col.colName === "years_of_experience" ? 100
                      : col.colName === "availability" ? 140
                      : col.colName === "current_compensation" ? 150
                      : col.colName === "work_preference" ? 150
                      : col.type === "file" ? 90
                      : 180;
                    return <th key={col.key} style={{ minWidth: colMinW }}>{col.label}</th>;
                  })}
                  <th style={{ minWidth: 160 }}>Job Title</th>
                  <th style={{ minWidth: 90 }}>Status</th>
                  <th style={{ minWidth: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...filteredApps].sort((a, b) => {
                  const da = a.applied_date || a.created_at || "";
                  const db = b.applied_date || b.created_at || "";
                  return sortOrder === "newest" ? db.localeCompare(da) : da.localeCompare(db);
                }).map((app) => (
                  <React.Fragment key={app.id}>
                    <tr
                      style={{ background: app.status === "pending" ? "#fffbe6" : app.status === "rejected" ? "#fff0f0" : "transparent", cursor: "pointer" }}
                      onClick={() => { setSelectedApp(app); setShowDetailsModal(true); }}
                      onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(0.97)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
                    >
                      <td style={{ fontSize: 13, color: "#64748b", whiteSpace: "nowrap", minWidth: 150 }}>{formatDate(app.created_at)}</td>
                      {columnOrder.map(col => {
                        const colMinW = col.colName === "full_name" ? 160
                          : col.colName === "email" ? 200
                          : col.colName === "phone" ? 140
                          : col.colName === "linkedin_url" ? 150
                          : col.colName === "years_of_experience" ? 100
                          : col.colName === "availability" ? 140
                          : col.colName === "current_compensation" ? 150
                          : col.colName === "work_preference" ? 150
                          : col.type === "file" ? 90
                          : 180;
                        switch (col.colName) {
                          case "full_name":
                            return <td key={col.key} style={{ minWidth: colMinW }}><strong>{app.full_name}</strong></td>;
                          case "email":
                            return <td key={col.key} style={{ fontSize: 13, whiteSpace: "normal", wordBreak: "break-word", minWidth: colMinW }}>{app.email}</td>;
                          case "phone":
                            return <td key={col.key} style={{ fontSize: 13, whiteSpace: "nowrap", minWidth: colMinW }}>{app.phone || "-"}</td>;
                          case "linkedin_url":
                            return (
                              <td key={col.key} style={{ fontSize: 13, whiteSpace: "normal", wordBreak: "break-word", minWidth: colMinW }}>
                                {app.linkedin_url ? (
                                  <a className="AdminLink" href={app.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>Link</a>
                                ) : "-"}
                              </td>
                            );
                          case "years_of_experience":
                            return <td key={col.key} style={{ fontSize: 13, minWidth: colMinW }}>{app.years_of_experience || "-"}</td>;
                          case "availability":
                            return <td key={col.key} style={{ fontSize: 13, minWidth: colMinW }}>{app.availability || "-"}</td>;
                          case "current_compensation":
                            return <td key={col.key} style={{ fontSize: 13, minWidth: colMinW }}>{app.current_compensation || "-"}</td>;
                          case "work_preference":
                            return <td key={col.key} style={{ fontSize: 13, minWidth: colMinW }}>{app.work_preference || "-"}</td>;
                          default:
                            if (col.type === "file") {
                              const resumeUrl = getResumeUrl(app);
                              return (
                                <td key={col.key} style={{ fontSize: 13, minWidth: colMinW }}>
                                  {resumeUrl ? (
                                    <a className="AdminLink" href={resumeUrl} target="_blank" rel="noopener noreferrer">View</a>
                                  ) : "-"}
                                </td>
                              );
                            }
                            const val = getFormValue(app, col);
                            const isTextarea = col.type === "textarea";
                            return (
                              <td key={col.key} className={isTextarea ? "AdminTextClamp" : ""} style={{ fontSize: 13, whiteSpace: "normal", wordBreak: "break-word", minWidth: colMinW }} title={val || ""}>
                                {val != null && val !== "" ? val : "—"}
                              </td>
                            );
                        }
                      })}
                      <td style={{ fontSize: 13, whiteSpace: "normal", wordBreak: "break-word", minWidth: 160 }}>{app.job_title}</td>
                      <td style={{ minWidth: 90 }}>
                        <span className={`AdminStatusBadge AdminStatus${app.status === "accepted" ? "Approved" : app.status === "rejected" ? "Rejected" : app.status === "reviewed" ? "Pending" : "Default"}`}>
                          {app.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          {(app.status === "pending" || app.status === "reviewed") && (
                            <>
                              <button title="Accept"
                                style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#16a34a", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}
                                onClick={() => handleStatusChange(app.id, "accepted")}
                              >
                                &#10003;
                              </button>
                              <button title="Reject"
                                style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}
                                onClick={() => handleStatusChange(app.id, "rejected")}
                              >
                                &#10005;
                              </button>
                            </>
                          )}
                          {app.status === "pending" && (
                            <button title="Mark Reviewed"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#2563eb", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => handleStatusChange(app.id, "reviewed")}
                            >
                              &#9998;
                            </button>
                          )}

                          {app.status !== "pending" && (
                            <button title="Delete"
                              style={{ width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => handleDelete(app.id)}
                            >
                              &#128465;
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedId === app.id && (
                      <tr>
                        <td colSpan={columnOrder.length + 4} style={{ background: "#f8fafc", padding: "16px 24px" }}>
                          <button className="AdminButton AdminButtonSmall AdminButtonDanger"
                            onClick={() => handleStatusChange(app.id, "accepted")}
                          >
                            Restore (Move to Accepted)
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </TableWithScrollButtons>
        )}
      </div>

      {/* ── Application Details Modal ── */}
      {showDetailsModal && selectedApp && (
        <div className="AdminModal" onClick={() => setShowDetailsModal(false)}>
          <div className="AdminModalContent" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", position: "relative" }}>
            <button
              onClick={() => setShowDetailsModal(false)}
              style={{ position: "absolute", top: 12, right: 16, background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#64748b", lineHeight: 1 }}
              title="Close"
            >
              &times;
            </button>
            <h3 className="AdminModalTitle" style={{ marginBottom: 20 }}>Application Details</h3>
            <div style={{ overflowY: "auto", flex: 1, paddingRight: 4 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", minWidth: 0 }}>
                {formFields.map((f) => {
                  const val = getFormValue(selectedApp, f);
                  const label = f.label.replace(/\s*\*$/, "");
                  if (f.type === "file") {
                    const resumeUrl = getResumeUrl(selectedApp);
                    return (
                      <div key={f.key}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{label}</label>
                        <div>
                          {resumeUrl ? (
                            <a className="AdminLink" href={resumeUrl} target="_blank" rel="noopener noreferrer">View Resume</a>
                          ) : <span style={{ color: "#94a3b8" }}>—</span>}
                        </div>
                      </div>
                    );
                  }
                  const isLong = val && val.length > 60;
                  const isLinkedIn = f.key?.toLowerCase().includes("linkedin") || f.label?.toLowerCase().includes("linkedin");
                  const isUrl = val && (String(val).startsWith("http://") || String(val).startsWith("https://"));
                  return (
                    <div key={f.key} style={{ ...(isLong ? { gridColumn: "1 / -1" } : {}), minWidth: 0, overflowWrap: "break-word" }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{label}</label>
                      <div style={{ fontSize: 14, color: "#1e293b", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {val != null && val !== "" ? (
                          isUrl ? <a className="AdminLink" href={String(val)} target="_blank" rel="noopener noreferrer">{isLinkedIn ? "LinkedIn Profile" : "Link"}</a>
                        : val) : <span style={{ color: "#94a3b8" }}>—</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Orphaned form_data entries that don't match current form field keys */}
              {(() => {
                const currentKeys = new Set(formFields.map(f => f.key));
                const orphaned = Object.entries(selectedApp.form_data || {}).filter(([k]) => !currentKeys.has(k));
                if (orphaned.length === 0) return null;
                return (
                  <>
                    <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>Additional Data</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", minWidth: 0 }}>
                      {orphaned.map(([key, val]) => (
                        <div key={key} style={{ minWidth: 0, overflowWrap: "break-word" }}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                            {key}
                          </label>
                          <div style={{ fontSize: 14, color: "#1e293b", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {val != null && val !== "" ? val : <span style={{ color: "#94a3b8" }}>—</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
              <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 24px" }}>
                <div style={{ textAlign: "center" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Job Title</label>
                  <div style={{ fontSize: 14, color: "#1e293b" }}>{selectedApp.job_title || "—"}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Status</label>
                  <div>
                    <span className={`AdminStatusBadge AdminStatus${selectedApp.status === "accepted" ? "Approved" : selectedApp.status === "rejected" ? "Rejected" : selectedApp.status === "reviewed" ? "Pending" : "Default"}`}>
                      {selectedApp.status}
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Applied</label>
                  <div style={{ fontSize: 14, color: "#1e293b" }}>{formatDate(selectedApp.created_at)}</div>
                </div>
              </div>
            </div>
            <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />
            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>Actions</h4>
            <div style={{ textAlign: "center" }}>
              {(selectedApp.status === "pending" || selectedApp.status === "reviewed") && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#d1fae5", color: "#065f46", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={() => { handleStatusChange(selectedApp.id, "accepted"); setShowDetailsModal(false); }}>
                  Accept
                </button>
              )}
              {selectedApp.status === "pending" && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#fef3c7", color: "#92400e", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={() => { handleStatusChange(selectedApp.id, "reviewed"); setShowDetailsModal(false); }}>
                  Mark Reviewed
                </button>
              )}
              {(selectedApp.status === "pending" || selectedApp.status === "reviewed") && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#fee2e2", color: "#991b1b", border: "none", display: "inline-flex", margin: 4 }}
                  onClick={() => { handleStatusChange(selectedApp.id, "rejected"); setShowDetailsModal(false); }}>
                  Reject
                </button>
              )}
              {selectedApp.status !== "pending" && (
                <button className="AdminButton AdminButtonSmall AdminButtonDanger" style={{ display: "inline-flex", margin: 4 }}
                  onClick={() => { const id = selectedApp.id; setShowDetailsModal(false); handleDelete(id); }}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminJobApplications;
